import type { Response } from '@/api/axiosInstance';
import { UploadFileResourceResponse } from '@/api/file';
import { InboxOutlined } from '@ant-design/icons';
import { App, Form, Modal, Tag, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UploadDialogProps {
    visible: boolean;
    targetDir?: string;
    onSubmit: (files: UploadFile[], path: string) => Promise<Response<UploadFileResourceResponse> | undefined>;
    onClose: () => void;
}

const UploadDialog: React.FC<UploadDialogProps> = ({ visible, targetDir, onSubmit, onClose }) => {
    const { message } = App.useApp();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const { t } = useTranslation('common');

    const handleChange = (info: { fileList: UploadFile[] }) => {
        setFileList([...info.fileList]);
    };

    const handleSubmit = async () => {
        if (fileList.length === 0 || !targetDir) {
            return;
        }
        const res = await onSubmit(fileList, targetDir);
        if (res?.ok) {
            if(res.data?.hasConflicts) {
                message.warning(`${res.data?.conflictFiles?.join("\n") ?? "Files"} already exists`);
                return;
            }
            message.success(t('upload_dialog.success'));
            setFileList([]);
            onClose();
        } else {
            message.error(<span className=' whitespace-pre-wrap'>{res?.message ?? t('upload_dialog.failed')}</span>);
        }
    };

    const handleCancel = () => {
        setFileList([]);
        onClose();
    }

    return (
        <Modal open={visible} title={t('upload_dialog.title')} onCancel={handleCancel} onOk={handleSubmit}>
            <Form.Item label={t('upload_dialog.current_file')}>
                <Tag>{targetDir ?? "/home"}</Tag>
            </Form.Item>
            <Upload.Dragger
                className='h-80 mt-4'
                multiple
                beforeUpload={() => false}
                fileList={fileList}
                onChange={handleChange}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-drag-icon"></p>
                <p className="ant-upload-text">{t('upload_dialog.drag_drop')}</p>
            </Upload.Dragger>
        </Modal>
    );
};

export default UploadDialog;